import React from 'react';
import Bugsnag from '@bugsnag/js';
import { DesignerCard_UserFragment } from '@99designs/apps/spa/pages/create/components/DesignerCard.generated';
import {
    trackAddedDesignerEvent,
    trackRemovedDesignerEvent,
} from '@99designs/apps/spa/pages/create/events';
import AppendStructuredBriefDesigners from '@99designs/apps/spa/pages/create/mutations/AppendStructuredBriefDesigners';
import RemoveStructuredBriefDesigners from '@99designs/apps/spa/pages/create/mutations/RemoveStructuredBriefDesigners';
import { Box } from '@99designs/emu';
import { colors } from '@99designs/emu/settings';
import { Button } from '@99designs/frontend-components/components/Button';
import { DesignerSource } from '@99designs/graph-utils/types';
import { IconTick2 } from '@99designs/icons';

export interface SelectDesignerLabels {
    selected: string;
    notSelected: string;
}

interface Props {
    designer: DesignerCard_UserFragment;
    position?: number;
    designerSource: DesignerSource;
    onAddDesigner?: () => void;
    selectDesignerLabels?: SelectDesignerLabels;
    totalAllowableDesigners: number;
    brief: {
        id: string;
        designers: {
            id: string;
        }[];
    };
    color?: colors.Color;
}

const AddDesignerButton = ({
    designer,
    position,
    designerSource,
    onAddDesigner,
    selectDesignerLabels = {
        selected: __('Added'),
        notSelected: __('Add'),
    },
    color = 'grey800',
    brief,
    totalAllowableDesigners,
}: Props) => {
    return (
        <AppendStructuredBriefDesigners>
            {(appendDesigners) => (
                <RemoveStructuredBriefDesigners>
                    {(removeDesigners) => {
                        const isSuspended = designer.penaltyState === 'SUSPENDED';
                        // eslint-disable-next-line eqeqeq
                        const isSelected = brief.designers.some((d) => d.id == designer.id);
                        const shouldDisable =
                            brief.designers.length === totalAllowableDesigners || isSuspended;

                        return (
                            <Box color={color}>
                                <Button
                                    data-testid="add-designer-button"
                                    isDisabled={shouldDisable && !isSelected}
                                    onClick={
                                        isSelected
                                            ? () => {
                                                  trackRemovedDesignerEvent({
                                                      id: brief.id,
                                                      relevant_ranking: position,
                                                      designer_id: parseInt(designer.id),
                                                  });
                                                  removeDesigners({
                                                      id: brief.id,
                                                      designerIds: [parseInt(designer.id)],
                                                  }).catch(Bugsnag.notify);
                                              }
                                            : () => {
                                                  trackAddedDesignerEvent({
                                                      id: brief.id,
                                                      relevant_ranking: position,
                                                      designer_id: parseInt(designer.id),
                                                      designer_name: designer.displayName,
                                                      designer_level:
                                                          designer.profile.designerLevel,
                                                      designer_source: designerSource,
                                                  });

                                                  appendDesigners({
                                                      id: brief.id,
                                                      designerIds: [parseInt(designer.id)],
                                                      source: designerSource,
                                                  })
                                                      .then(() => {
                                                          if (onAddDesigner) {
                                                              onAddDesigner();
                                                          }
                                                      })
                                                      .catch(Bugsnag.notify);
                                              }
                                    }
                                    variations={
                                        isSelected
                                            ? ['fluid', 'primary', 'chameleon']
                                            : ['fluid', 'secondary']
                                    }
                                    icon={isSelected ? <IconTick2 /> : undefined}
                                    iconAlignment="right"
                                >
                                    {isSuspended
                                        ? __('Designer suspended')
                                        : isSelected
                                        ? selectDesignerLabels.selected
                                        : selectDesignerLabels.notSelected}
                                </Button>
                            </Box>
                        );
                    }}
                </RemoveStructuredBriefDesigners>
            )}
        </AppendStructuredBriefDesigners>
    );
};

export default AddDesignerButton;
